import { ref } from 'vue';
import type { Incident, ChatMessage, NewChatMessageBody } from '../types';
import { useIncidentHub } from '@/composables/useIncidentHub';
import { ChatUserType } from '../enums/ChatUserType';
import { EmergencySituationInstructions } from '../enums/EmergencySituationInstructions';

const incidentHub = useIncidentHub();

const messages = ref<ChatMessage[]>([]);
const dispatcherChatTextMessagePreview = ref<string | null>("");
const translationsAllowed = ref<boolean>(false);

export function useChat() {
  async function loadChat(incident: Incident) {
    const incidentHubConnection = await incidentHub.startSignalRConnection();
    incidentHubConnection.on("NewMessageReceived", onNewMessageReceived);
    incidentHubConnection.on("DispatcherChatTextMessagePreviewReceived", onDispatcherChatTextMessagePreviewReceived);
    incidentHubConnection.on("ChatTranslationsAllowedChanged", onChatTranslationsAllowedChanged);

    translationsAllowed.value = await incidentHubConnection.invoke("IsChatTranslationsAllowed");
    dispatcherChatTextMessagePreview.value = await incidentHub.getDispatcherChatTextMessagePreview();

    messages.value = await Promise.all(incident.chatMessages.map(mapToChatMessage));
  }

  async function onNewMessageReceived(message: ChatMessage) {
    messages.value.push(await mapToChatMessage(message));
  }

  function onDispatcherChatTextMessagePreviewReceived(message: string) {
    dispatcherChatTextMessagePreview.value = message;
  }

  function onChatTranslationsAllowedChanged(newValue: boolean) {
    translationsAllowed.value = newValue;
  }

  async function mapToChatMessage(msg: any): Promise<ChatMessage> {
    return ({
      id: msg.id,
      sentTimeUtc: new Date(msg.sentTimeUtc),
      userType: msg.userType,
      message: msg.message,
      translatedMessage: await incidentHub.translateText(msg.message),
      emergencySituationInstructions: msg.emergencySituationInstructions,
      attachedImageUrl: msg.attachedImageUrl ?? null,
    });
  }

  async function sendPreview(messageText: string) {
    await incidentHub.sendChatPreview(messageText);
  }

  async function sendMessage(messageText: string) {
    const newMessage: NewChatMessageBody = {
      message: messageText,
      emergencySituationInstructions: EmergencySituationInstructions.None,
      attachment: undefined,
    };

    await incidentHub.sendChatMessage(newMessage);
  }

  return {
    messages,
    dispatcherChatTextMessagePreview,
    translationsAllowed,
    loadChat,
    sendMessage,
    sendPreview,
  };
}
